// Central site content for formats, verticals, features, FAQs.
import {
  Zap, ArrowRightLeft, Globe2, BellRing, AppWindow, Send,
  Dice5, LineChart, ShoppingBag, Gift, Wrench, Shield, Users,
  Sparkles, Crosshair, ShieldCheck, MapPin, UserRoundCog, Activity, ListChecks, GaugeCircle,
  Brain, Coins,
} from 'lucide-react';

export const FORMATS = [
  {
    slug: 'zero-click',
    icon: Zap,
    title: 'Zero-Click Traffic',
    short: 'Route high-intent users directly to advertiser destinations — no creatives required.',
    bestFor: 'Direct-response offers, fast funnel testing',
    benefits: [
      'No creative assets needed to launch',
      'Fast testing of landing pages and funnels',
      'Strong fit for direct-response and lead-gen offers',
      'Works smoothly with postback-based tracking',
      'Reaches high-intent users with short paths to action',
    ],
    bestVerticals: ['iGaming', 'Sweepstakes', 'Finance', 'Software & Utilities'],
    optimization: [
      'Validate landing pages before scaling daily caps',
      'Use source-level reporting and trim weak placements early',
      'Layer GEO + device rules once you have a clear winner',
    ],
    faq: [
      { q: 'Do I need ad creatives for zero-click?', a: 'No — zero-click sends users directly to your destination URL based on traffic intent.' },
      { q: 'Which trackers work best?', a: 'Voluum, RedTrack, BeMob, Keitaro, Binom and any postback-based tracker work well.' },
      { q: 'How fast can I launch?', a: 'After application review, most advertisers can launch within 1–3 business days.' },
    ],
  },
  {
    slug: 'domain-redirect',
    icon: ArrowRightLeft,
    title: 'Domain Redirect Traffic',
    short: 'Capture type-in and expired-domain traffic and route users to relevant campaigns.',
    bestFor: 'Intent-driven user flow, vertical-specific scaling',
    benefits: [
      'Intent-based user flow from type-in traffic',
      'High relevance for direct navigation users',
      'Scales well alongside other formats',
      'Optimizable by domain and source quality',
      'Compatible with most tracker setups',
    ],
    bestVerticals: ['Finance', 'iGaming', 'Sweepstakes', 'VPN'],
    optimization: [
      'Use category-aligned destinations to keep relevance high',
      'Whitelist top-performing domains after first 100 conversions',
      'A/B test landing pages by GEO',
    ],
    faq: [
      { q: 'Is domain redirect different from zero-click?', a: 'Domain redirect specifically uses domain-level intent signals, while zero-click can also include other source-based intent flows.' },
      { q: 'Do you support GEO targeting?', a: 'Yes — you can specify target GEOs during application and refine after launch.' },
    ],
  },
  {
    slug: 'pop',
    icon: Globe2,
    title: 'Pop Traffic',
    short: 'Scale reach with cost-efficient pop placements suitable for testing and performance.',
    bestFor: 'Volume testing, retargeting, broad performance scaling',
    benefits: [
      'High volume potential at competitive CPMs',
      'Great for testing landing pages and offers',
      'Cost-efficient way to validate funnels',
      'Works across most non-restricted verticals',
      'Granular source filtering supported',
    ],
    bestVerticals: ['Sweepstakes', 'E-commerce', 'Software & Utilities', 'VPN'],
    optimization: [
      'Cap daily spend until you have a meaningful sample',
      'Use blacklists aggressively in the first 7 days',
      'Re-test blacklisted sources every 30 days',
    ],
    faq: [
      { q: 'Is pop traffic still effective?', a: 'For testing landing pages and scaling proven funnels, yes — with proper source filtering.' },
      { q: 'Will I see bot traffic?', a: 'Filtering layers reduce bot exposure. We do not claim 100% fraud-free traffic, but quality controls run continuously.' },
    ],
  },
  {
    slug: 'push',
    icon: BellRing,
    title: 'Push Traffic',
    short: 'Notification-style placements built for quick attention and direct response.',
    bestFor: 'Engagement-driven offers, creative-led testing',
    benefits: [
      'Engaging notification format with strong attention',
      'Headline + creative levers for fast iteration',
      'Good for offers needing quick user action',
      'Works across finance, utilities, sweepstakes and more',
      'Source-level data lets you scale winners quickly',
    ],
    bestVerticals: ['Finance', 'iGaming', 'Sweepstakes', 'Nutra'],
    optimization: [
      'Refresh creatives every 5–7 days to fight fatigue',
      'Test headlines first — they move CTR more than icons',
      'Layer device + browser rules after first scale step',
    ],
    faq: [
      { q: 'Do I need new creatives often?', a: 'Yes — push performs best with frequent creative rotation.' },
      { q: 'Can I run on mobile only?', a: 'Yes, device-level targeting is supported.' },
    ],
  },
  {
    slug: 'in-page-push',
    icon: AppWindow,
    title: 'In-Page Push',
    short: 'Notification-style placements that appear directly on websites — no subscription required.',
    bestFor: 'Browser-friendly reach, mobile-first campaigns',
    benefits: [
      'Works without browser subscription requirements',
      'Reaches more environments, including iOS',
      'Native-like appearance increases engagement',
      'Good for both mobile and desktop campaigns',
      'Simple creative requirements',
    ],
    bestVerticals: ['Finance', 'iGaming', 'E-commerce', 'Software & Utilities'],
    optimization: [
      'Test mobile and desktop separately',
      'Use clear, scannable headlines',
      'Watch dwell time per source for quality signals',
    ],
    faq: [
      { q: 'How is in-page push different from regular push?', a: 'In-page push appears directly on websites without requiring a browser subscription, expanding reach across more environments.' },
      { q: 'Is iOS supported?', a: 'Yes — in-page push reaches iOS users that classic push cannot.' },
    ],
  },
  {
    slug: 'direct-click',
    icon: Send,
    title: 'Direct Click / Smart Redirect',
    short: 'Send users directly to advertiser landing pages without creative assets.',
    bestFor: 'Funnel testing, vertical-aware routing',
    benefits: [
      'Zero creative dependency to launch',
      'Smart routing rules by intent and source',
      'Direct path to conversion event',
      'Pairs well with postback tracking',
      'Good for fast vertical exploration',
    ],
    bestVerticals: ['iGaming', 'Sweepstakes', 'Finance', 'VPN'],
    optimization: [
      'Begin with broad routing, then narrow by GEO and device',
      'Tighten source list once a clear winner emerges',
    ],
    faq: [
      { q: 'Is direct click suitable for new offers?', a: 'Yes — it lets you test landers without producing creatives first.' },
    ],
  },
];

export const VERTICALS = [
  {
    slug: 'igaming',
    icon: Dice5,
    title: 'iGaming',
    intro: 'Casino, sports betting, and gaming brands use Ads Dashbound to acquire registrations, FTDs and reactivations through compliant traffic flows.',
    formats: ['Push', 'In-Page Push', 'Pop', 'Domain Redirect', 'Zero-Click'],
    goals: ['Casino lead generation', 'Sports betting acquisition', 'Registration campaigns', 'First-time depositor flows'],
    optimization: ['Start with permitted GEOs only', 'Use creative variants for sports vs casino', 'Cap daily budget for first 72h'],
    disclaimer: 'Advertisers are responsible for ensuring offers are legally permitted in target GEOs and that creatives comply with local regulations.',
  },
  {
    slug: 'finance',
    icon: LineChart,
    title: 'Finance & Trading',
    intro: 'Trading education, fintech apps, lending and investment offers run on Ads Dashbound with compliance-first creative guidelines.',
    formats: ['Push', 'In-Page Push', 'Pop', 'Domain Redirect'],
    goals: ['Trading education funnels', 'Finance lead generation', 'Fintech app installs', 'Credit and loan offers where permitted'],
    optimization: ['Use disclosure-friendly headlines', 'Avoid income guarantees', 'Run separate creatives per regulator GEO'],
    disclaimer: 'Financial advertisers must use clear, compliant messaging and avoid misleading income or investment claims.',
  },
  {
    slug: 'ecommerce',
    icon: ShoppingBag,
    title: 'E-commerce',
    intro: 'Direct-to-consumer brands and marketplaces use Ads Dashbound for product launches, seasonal pushes and lead capture funnels.',
    formats: ['Pop', 'Push', 'In-Page Push', 'Domain Redirect'],
    goals: ['Product launch campaigns', 'Marketplace offer scaling', 'Seasonal promotions', 'Lead capture funnels'],
    optimization: ['Match creatives to promotion windows', 'Test mobile-first landers', 'Use device + GEO splits early'],
  },
  {
    slug: 'sweepstakes',
    icon: Gift,
    title: 'Sweepstakes',
    intro: 'Email and SOI funnels are a strong fit for our broad-reach formats. Run mobile-first creatives and watch source quality closely.',
    formats: ['Pop', 'Push', 'In-Page Push', 'Zero-Click'],
    goals: ['Lead generation', 'Survey funnels', 'Email submit campaigns', 'Mobile-first landing pages'],
    optimization: ['Mobile-first design is non-negotiable', 'Use blacklists aggressively in first week', 'Localize prizes and language'],
  },
  {
    slug: 'software-utilities',
    icon: Wrench,
    title: 'Software & Utilities',
    intro: 'Browser tools, security software, productivity apps and other utility offers scale on Ads Dashbound with simple creative requirements.',
    formats: ['Pop', 'Push', 'In-Page Push', 'Direct Click'],
    goals: ['App installs', 'Browser tool installs', 'Security software signups', 'Productivity software trials'],
    optimization: ['Run install-rate-focused creatives', 'A/B test value props in headlines', 'Track post-install events when possible'],
  },
  {
    slug: 'vpn',
    icon: Shield,
    title: 'VPN',
    intro: 'Privacy-focused offers, subscription trials and app installs perform well across our notification-style formats.',
    formats: ['Push', 'In-Page Push', 'Pop', 'Domain Redirect'],
    goals: ['Privacy-focused campaigns', 'Subscription offers', 'Trial signups', 'App installs'],
    optimization: ['Lead with privacy and speed angles', 'Run creative tests by region', 'Use device-level rules for app installs'],
  },
  {
    slug: 'lead-generation',
    icon: Users,
    title: 'Lead Generation',
    intro: 'Form-fill and SOI funnels across home services, education, insurance, and B2B \u2014 with source-level controls to keep lead quality high.',
    formats: ['Pop', 'Push', 'In-Page Push', 'Domain Redirect', 'Zero-Click'],
    goals: ['Email submit funnels', 'Quote-request flows', 'Multi-step lead capture', 'Survey-based generation'],
    optimization: ['Cap budget per source until lead quality is validated', 'Use device + GEO splits early', 'Refresh creatives every 7 days'],
    faq: [
      { q: 'Which formats work best for lead generation?', a: 'Zero-click and direct click work well for short, high-intent funnels. Push and in-page push are strong for creative-led tests, while pop is useful for broader volume testing.' },
      { q: 'Can I scale on a strict CPL?', a: 'Yes. Source-level controls let you cap budget per source until lead quality is validated, then scale only the placements that hit your CPL target.' },
      { q: 'Do you support SOI and DOI funnels?', a: 'Yes — both single opt-in and double opt-in funnels are supported, depending on the offer, GEO and tracker setup.' },
      { q: 'What about lead quality?', a: 'We run continuous quality controls and let you blacklist underperforming sources. We do not promise specific approval or close rates — those depend on your funnel and offer.' },
    ],
    relatedFormats: ['zero-click', 'push', 'pop', 'direct-click'],
  },
];

export const FEATURES = [
  {
    slug: 'ai-traffic-intelligence',
    icon: Brain,
    title: 'AI Traffic Intelligence',
    short: 'AI-assisted source scoring, smart GEO and device routing, and fraud signal detection for cleaner traffic flows.',
    points: [
      'AI-assisted source scoring across formats',
      'Smart GEO and device routing recommendations',
      'Fraud and bot signal detection layers',
      'Budget flow suggestions tuned to performance',
      'Continuous learning from advertiser-level signals',
    ],
    note: 'AI Traffic Intelligence pairs platform tooling with human review. We do not claim fully autonomous AI media buying — humans stay in the loop on every approved campaign.',
    faq: [
      { q: 'Is the optimization fully automated?', a: 'No. AI Traffic Intelligence assists routing and source scoring, but a human reviewer sets up campaigns and confirms format / GEO recommendations during onboarding.' },
      { q: 'Will AI replace my account manager?', a: 'No. AI signals support your account manager — you still get direct human guidance on launch, scaling and source rules.' },
      { q: 'Does AI prevent all ad fraud?', a: 'No. AI signals reduce exposure to bot and invalid traffic, but we do not claim 100% fraud-free delivery.' },
    ],
    relatedFeatures: ['smart-optimization', 'fraud-filtering', 'source-level-control'],
    relatedFormats: ['zero-click', 'push', 'in-page-push'],
  },
  {
    slug: 'source-level-control',
    icon: Crosshair,
    title: 'Source-Level Control',
    short: 'Source-level reporting, whitelist and blacklist support, and placement filtering for cleaner campaign paths.',
    points: [
      'Source-level reporting visibility',
      'Whitelist controls for top performers',
      'Blacklist controls for low-quality placements',
      'Placement and source filtering rules',
      'Better quality decisions backed by real data',
    ],
    faq: [
      { q: 'Can I whitelist or blacklist sources myself?', a: 'Yes — your account manager helps you apply whitelist and blacklist rules at the source and placement level after you have meaningful data.' },
      { q: 'When should I start using whitelists?', a: 'After roughly 100 conversions or one full week of spend, depending on volume. Your account manager will guide the timing.' },
      { q: 'Does source-level control work on every format?', a: 'Yes. It is available across zero-click, domain redirect, pop, push, in-page push and direct click formats.' },
    ],
    relatedFeatures: ['fraud-filtering', 'ai-traffic-intelligence'],
    relatedFormats: ['zero-click', 'pop', 'push', 'in-page-push', 'domain-redirect'],
  },
  {
    slug: 'fraud-filtering',
    icon: ShieldCheck,
    title: 'Fraud Filtering',
    short: 'Bot filtering, suspicious traffic review, and continuous quality controls across the network.',
    points: [
      'Bot filtering at multiple layers',
      'Suspicious traffic review and source monitoring',
      'Continuous quality controls',
      'Clean campaign routing logic',
      'Honest, transparent reporting',
    ],
    note: 'Quality controls reduce exposure to invalid traffic. We do not claim 100% fraud-free traffic.',
    faq: [
      { q: 'Do you guarantee fraud-free traffic?', a: 'No. We run continuous quality controls but do not claim 100% fraud-free delivery — anyone who does should be treated with caution.' },
      { q: 'How do you detect bot traffic?', a: 'A combination of source-level signals, behavioral patterns, and platform filters help flag and remove suspicious traffic before it reaches advertiser destinations.' },
      { q: 'Can I see source-level quality data?', a: 'Yes — source-level reporting is available so you can audit performance and apply whitelist or blacklist rules.' },
    ],
    relatedFeatures: ['source-level-control', 'ai-traffic-intelligence'],
    relatedFormats: ['push', 'in-page-push', 'pop'],
  },
  {
    slug: 'top-up-model',
    icon: Coins,
    title: 'Top-Up Advertising Model',
    short: 'No subscription plans. Approved advertisers fund campaigns with flexible top-ups based on traffic format, GEO and budget.',
    points: [
      'No public pricing tables or fixed subscription plans',
      'Top-up minimums depend on format, GEO and vertical',
      'Funds go directly toward campaign delivery',
      'Manual advertiser review before any top-up',
      'Account manager guidance on recommended ranges',
    ],
    note: 'Top-up minimums and recommended ranges are shared during onboarding. We do not publish pricing tables because the right number depends on your offers, GEOs and traffic format mix.',
    faq: [
      { q: 'Do you have fixed pricing plans?', a: 'No. Ads Dashbound uses a top-up model — there are no public pricing tables or monthly subscription tiers.' },
      { q: 'What is the minimum top-up?', a: 'Top-up minimums depend on format, GEO and vertical. Your account manager shares recommended ranges during onboarding.' },
      { q: 'Are payments handled on this website?', a: 'No. Payments and campaign funding are handled separately after approval — not through this marketing site.' },
      { q: 'Do top-ups expire?', a: 'No. Approved advertiser balances are used for campaign delivery and remain available across active campaigns.' },
    ],
    relatedFeatures: ['smart-optimization', 'ai-traffic-intelligence'],
    relatedFormats: [],
  },
  {
    slug: 'smart-optimization',
    icon: Sparkles,
    title: 'Smart Optimization',
    short: 'Campaign performance review, source-level decisioning, and bid + budget guidance from real humans.',
    points: [
      'Campaign performance review during onboarding',
      'Source-level decisioning recommendations',
      'Bid and budget guidance with launch checklists',
      'Format and GEO recommendations',
      'Scaling strategy supported by your account manager',
    ],
    note: 'We use the term “smart optimization” to describe a mix of platform tooling and human review. We do not claim fully automated AI optimization.',
    faq: [
      { q: 'Is smart optimization fully automated?', a: 'No. It blends platform tooling with hands-on human review, especially during the first 72 hours of launch.' },
      { q: 'Will I get scaling guidance?', a: 'Yes — your account manager provides scaling steps, source rules and budget guidance after early performance signals.' },
    ],
    relatedFeatures: ['ai-traffic-intelligence', 'source-level-control', 'top-up-model'],
    relatedFormats: ['push', 'in-page-push', 'pop'],
  },
];

export const WHY_DASHBOUND = [
  { icon: Globe2, title: 'Curated Traffic Access', text: 'Hand-picked supply across formats and verticals — not just raw inventory.' },
  { icon: Sparkles, title: 'Smart Optimization Guidance', text: 'Launch checklists, scaling steps, and source-level recommendations from real reviewers.' },
  { icon: ShieldCheck, title: 'Bot & Fraud Filtering', text: 'Continuous quality controls without overpromising 100% fraud-free claims.' },
  { icon: Crosshair, title: 'Source-Level Control', text: 'Whitelist, blacklist, and placement filtering give you the levers that matter.' },
  { icon: ListChecks, title: 'Whitelist & Blacklist Support', text: 'Tight controls so winning sources scale and weak ones disappear.' },
  { icon: Activity, title: 'Fast Launch Support', text: 'Most approved advertisers go live within 1–3 business days of application.' },
  { icon: UserRoundCog, title: 'Human Account Managers', text: 'Direct contact with reviewers who actually understand performance funnels.' },
  { icon: MapPin, title: 'Vertical-Specific Guidance', text: 'GEO and format guidance tuned to iGaming, Finance, Sweepstakes and more.' },
];

export const FEATURE_GRID = [
  { icon: Sparkles, title: 'Smart Optimization', text: 'Campaign review, format and GEO recommendations, scaling guidance.' },
  { icon: Crosshair, title: 'Source-Level Controls', text: 'Whitelist, blacklist, and placement filtering with full visibility.' },
  { icon: ShieldCheck, title: 'Bot / Fraud Filtering', text: 'Continuous quality controls and source monitoring.' },
  { icon: MapPin, title: 'GEO Targeting', text: '150+ GEO targeting possibilities with vertical-aware routing.' },
  { icon: Zap, title: 'Format Recommendations', text: 'Pick the right traffic format for each offer and audience.' },
  { icon: UserRoundCog, title: 'Account Manager Support', text: 'Hands-on launch and scaling guidance from real humans.' },
  { icon: GaugeCircle, title: 'Campaign Scaling Guidance', text: 'Structured steps for taking winners from $250 to $10k+ daily spend.' },
  { icon: ListChecks, title: 'Tracking-Friendly Setup', text: 'Compatible with Voluum, RedTrack, BeMob, Keitaro, Binom, CPV Lab and more.' },
];

export const TRACKING = [
  'Voluum', 'RedTrack', 'BeMob', 'Keitaro', 'Binom', 'CPV Lab', 'Google Analytics', 'Postback / S2S',
];

export const STATS = [
  { label: 'GEO targeting possibilities', value: 150, suffix: '+' },
  { label: 'Performance traffic formats', value: 6, suffix: '' },
  { label: 'Source-level controls', value: 100, suffix: '%' },
  { label: 'Avg. launch turnaround', value: 72, suffix: 'h' },
];

export const HOW_IT_WORKS = [
  { step: 1, title: 'Apply as advertiser', text: 'Submit the application with your offers, GEOs and budget band.' },
  { step: 2, title: 'Share offers and goals', text: 'Walk our team through your funnel, tracking setup and KPIs.' },
  { step: 3, title: 'Pick traffic formats', text: 'Choose from zero-click, push, pop, in-page push, domain redirect.' },
  { step: 4, title: 'Get recommendations', text: 'Receive a launch checklist plus GEO and source guidance.' },
  { step: 5, title: 'Top up and launch', text: 'Fund your campaign balance and go live with active support.' },
];

export const FAQ_PREVIEW = [
  { q: 'What is Ads Dashbound?', a: 'A performance traffic platform helping advertisers access curated traffic formats with hands-on campaign support.' },
  { q: 'Is there a login dashboard on this site?', a: 'No — the advertiser panel is hosted separately. This site is for marketing, information and advertiser applications.' },
  { q: 'How do I start?', a: 'Submit the advertiser application form. Our team will review your offers, budget and traffic requirements.' },
  { q: 'Do you have fixed pricing plans?', a: 'No public pricing plans. Advertisers use a top-up based campaign funding model after approval.' },
  { q: 'What traffic formats are available?', a: 'Zero-click, domain redirect, pop, push, in-page push, and smart direct click.' },
  { q: 'Do you support tracking platforms?', a: 'Compatible with common tracking workflows like Voluum, RedTrack, BeMob, Keitaro, Binom, CPV Lab, Google Analytics and postback-based tracking.' },
  { q: 'Do you allow every type of offer?', a: 'No. Offers must follow our acceptable use policy and applicable laws.' },
];

export const FULL_FAQ_GROUPS = [
  {
    title: 'Getting Started',
    items: [
      { q: 'What is Ads Dashbound?', a: 'Ads Dashbound is a performance traffic platform helping advertisers access curated traffic formats and campaign support.' },
      { q: 'How do I start?', a: 'Submit the advertiser application form. Our team will review your offers, budget, and traffic requirements.' },
      { q: 'Is there an advertiser login dashboard on this website?', a: 'The advertiser panel is hosted separately. This website is for marketing, information, and advertiser applications.' },
      { q: 'How long does approval take?', a: 'Most applications are reviewed within 1–3 business days, depending on offer compliance and traffic goals.' },
    ],
  },
  {
    title: 'Funding & Pricing',
    items: [
      { q: 'Do you have fixed pricing plans?', a: 'No public pricing plans. Advertisers use a top-up based campaign funding model after approval.' },
      { q: 'What is the minimum top-up?', a: 'Top-up minimums depend on format and GEO. Your account manager will share recommended ranges during onboarding.' },
      { q: 'Are payments handled on this website?', a: 'No — payments and campaign funding are handled separately after approval, not on this marketing site.' },
    ],
  },
  {
    title: 'Formats, GEOs & Tracking',
    items: [
      { q: 'What traffic formats are available?', a: 'Zero-click, domain redirect, pop, push, in-page push, and smart direct click formats.' },
      { q: 'Can I choose GEOs?', a: 'Yes — share target GEOs during application and receive recommendations.' },
      { q: 'Do you support tracking platforms?', a: 'Compatible with common workflows: Voluum, RedTrack, BeMob, Keitaro, Binom, CPV Lab, GA, and postback-based tracking.' },
      { q: 'Can I bring my own postback URL?', a: 'Yes — server-to-server postback workflows are supported.' },
    ],
  },
  {
    title: 'Compliance & Support',
    items: [
      { q: 'Do you allow every type of offer?', a: 'No. Offers must follow Ads Dashbound’s acceptable use policy and applicable laws.' },
      { q: 'Do you provide account manager support?', a: 'Yes, managed support may be available depending on advertiser goals, spend and campaign needs.' },
      { q: 'Do you guarantee fraud-free traffic?', a: 'No. We run continuous quality controls but do not claim 100% fraud-free traffic.' },
    ],
  },
];

export const COUNTRIES_LIST = [
  'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 'France', 'Spain', 'Italy', 'Netherlands', 'Sweden',
  'Norway', 'Denmark', 'Finland', 'Poland', 'Portugal', 'Ireland', 'Switzerland', 'Austria', 'Belgium', 'Czech Republic',
  'Romania', 'Greece', 'Hungary', 'Bulgaria', 'Estonia', 'Latvia', 'Lithuania', 'Slovenia', 'Slovakia', 'Croatia',
  'Cyprus', 'Malta', 'Israel', 'United Arab Emirates', 'Saudi Arabia', 'Turkey', 'India', 'Singapore', 'Hong Kong', 'Japan',
  'South Korea', 'Vietnam', 'Thailand', 'Indonesia', 'Malaysia', 'Philippines', 'New Zealand', 'Brazil', 'Mexico', 'Argentina',
  'Chile', 'Colombia', 'Peru', 'Uruguay', 'South Africa', 'Nigeria', 'Kenya', 'Egypt', 'Morocco', 'Other',
];
